import { createBrowserRouter, Outlet } from 'react-router'

import AppShell from '@/components/layout/AppShell'

import RootPage from '@/app/page'
import LoginPage from '@/app/login/page'
import NotFoundPage from '@/app/not-found'
import RouteErrorBoundary from '@/src/RouteErrorBoundary'

// Books
import BooksPage from '@/app/books/page'
import BooksBankingPage from '@/app/books/banking/page'
import BooksBillsPage from '@/app/books/bills/page'
import BooksCoaPage from '@/app/books/coa/page'
import BooksContactsPage from '@/app/books/contacts/page'
import BooksExpensesPage from '@/app/books/expenses/page'
import BooksInvoicesPage from '@/app/books/invoices/page'
import BooksLedgerPage from '@/app/books/ledger/page'
import BooksReportsPage from '@/app/books/reports/page'
import BooksSettingsPage from '@/app/books/settings/page'

// Freight
import FreightBidsPage from '@/app/freight/bids/page'
import FreightLanesPage from '@/app/freight/lanes/page'
import FreightLoadsPage from '@/app/freight/loads/page'
import FreightOnboardingPage from '@/app/freight/onboarding/page'
import FreightShippersPage from '@/app/freight/shippers/page'

// System
import SystemApiKeysPage from '@/app/system/api-keys/page'
import SystemClassifiersPage from '@/app/system/classifiers/page'
import SystemErrorReportsPage from '@/app/system/error-reports/page'
import SystemFlagsPage from '@/app/system/flags/page'
import SystemHealthPage from '@/app/system/health/page'
import SystemIntegrationsPage from '@/app/system/integrations/page'
import SystemJobsPage from '@/app/system/jobs/page'
import SystemWebhooksPage from '@/app/system/webhooks/page'

// Tenants
import TenantsCarriersPage from '@/app/tenants/carriers/page'
import TenantsDriversPage from '@/app/tenants/drivers/page'
import TenantsOrganizationsPage from '@/app/tenants/organizations/page'
import TenantsOrganizationDetailPage from '@/app/tenants/organizations/[id]/page'
import TenantsProfilePage from '@/app/tenants/profile/page'
import TenantsBroadcastsPage from '@/app/tenants/broadcasts/page'
import TenantsGoodsPage from '@/app/tenants/goods/page'
import TenantsReferencePage from '@/app/tenants/reference/page'
import TenantsTripsPage from '@/app/tenants/trips/page'
import TenantsUsersPage from '@/app/tenants/users/page'
import TenantsVehiclesPage from '@/app/tenants/vehicles/page'

/**
 * Root layout. The app-wide chrome lives in AppShell, which already knows how
 * to hide itself on /login (`pathname.startsWith('/login')`). Auth gating is
 * also AppShell's job — see `useEffect` block there. So the router stays a
 * flat tree under one layout outlet.
 */
function RootLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  )
}

export const router = createBrowserRouter(
  [
    {
      element: <RootLayout />,
      errorElement: <RouteErrorBoundary />,
      children: [
        { path: '/', element: <RootPage /> },
        { path: '/login', element: <LoginPage /> },

        // Books
        { path: '/books', element: <BooksPage /> },
        { path: '/books/banking', element: <BooksBankingPage /> },
        { path: '/books/bills', element: <BooksBillsPage /> },
        { path: '/books/coa', element: <BooksCoaPage /> },
        { path: '/books/contacts', element: <BooksContactsPage /> },
        { path: '/books/expenses', element: <BooksExpensesPage /> },
        { path: '/books/invoices', element: <BooksInvoicesPage /> },
        { path: '/books/ledger', element: <BooksLedgerPage /> },
        { path: '/books/reports', element: <BooksReportsPage /> },
        { path: '/books/settings', element: <BooksSettingsPage /> },

        // Freight
        { path: '/freight/bids', element: <FreightBidsPage /> },
        { path: '/freight/lanes', element: <FreightLanesPage /> },
        { path: '/freight/loads', element: <FreightLoadsPage /> },
        { path: '/freight/onboarding', element: <FreightOnboardingPage /> },
        { path: '/freight/shippers', element: <FreightShippersPage /> },

        // System
        { path: '/system/api-keys', element: <SystemApiKeysPage /> },
        { path: '/system/classifiers', element: <SystemClassifiersPage /> },
        { path: '/system/error-reports', element: <SystemErrorReportsPage /> },
        { path: '/system/flags', element: <SystemFlagsPage /> },
        { path: '/system/health', element: <SystemHealthPage /> },
        { path: '/system/integrations', element: <SystemIntegrationsPage /> },
        { path: '/system/jobs', element: <SystemJobsPage /> },
        { path: '/system/webhooks', element: <SystemWebhooksPage /> },

        // Tenants
        { path: '/tenants/broadcasts', element: <TenantsBroadcastsPage /> },
        { path: '/tenants/goods', element: <TenantsGoodsPage /> },
        { path: '/tenants/carriers', element: <TenantsCarriersPage /> },
        { path: '/tenants/drivers', element: <TenantsDriversPage /> },
        { path: '/tenants/organizations', element: <TenantsOrganizationsPage /> },
        { path: '/tenants/organizations/:id', element: <TenantsOrganizationDetailPage /> },
        { path: '/tenants/profile', element: <TenantsProfilePage /> },
        { path: '/tenants/reference', element: <TenantsReferencePage /> },
        { path: '/tenants/trips', element: <TenantsTripsPage /> },
        { path: '/tenants/users', element: <TenantsUsersPage /> },
        { path: '/tenants/vehicles', element: <TenantsVehiclesPage /> },

        { path: '*', element: <NotFoundPage /> },
      ],
    },
  ],
  // basePath: rr-admin is served at /admin/* in production but at / in dev
  // (Vite `base` is conditional in vite.config.ts). `import.meta.env.BASE_URL`
  // gives Vite's effective base ('/admin/' for build, '/' for serve), which
  // React Router accepts as a basename when stripped of the trailing slash.
  { basename: import.meta.env.BASE_URL.replace(/\/$/, '') || '/' },
)
